import React, { useState, useEffect, useMemo } from 'react';
import { StyleSheet, Text, View, ScrollView, Dimensions, TouchableOpacity, useWindowDimensions } from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { Ionicons, AntDesign } from '@expo/vector-icons';
import Data, { authHeader } from './Data';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    LabelList,
} from 'recharts';
import moment from 'moment';
import { CSVLink } from "react-csv";
import { useRef } from "react";
import { ActivityIndicator } from 'react-native';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { getStorage, setStorage } from '../storage';
import { createPortal } from 'react-dom';
import { useScale, ScaleFn , useDarkMode} from './common';

// ─── 테마 토큰 ────────────────────────────────────────
const DARK = {
    bg:        '#0F172A',
    card:      '#1E293B',
    cardInner: '#0F172A',
    border:    'rgba(99,179,237,0.12)',
    text:      '#E2E8F0',
    textSub:   '#94A3B8',
    muted:     '#a8adb497',
    trackBg:   'rgba(255,255,255,0.05)',
};
const LIGHT = {
    bg:        '#F9FAFB',
    card:      '#FFFFFF',
    cardInner: '#F3F4F6',
    border:    '#E5E7EB',
    text:      '#111827',
    textSub:   '#4B5563',
    muted:     '#9CA3AF',
    trackBg:   'rgba(0,0,0,0.03)',
};

const S = {
    accent:  '#38BDF8',
    ok:      '#34D399',
    err:     '#F87171',
    warn:    '#FB923C',
    replace: '#FBBF24',
    purple:  '#A78BFA',
    pink:    '#F472B6',
};

const fmt = (v: any) => {
    if (v == null || v === '' || v === '-') return '-';
    const n = Number(v);
    return isNaN(n) ? v : n.toLocaleString();
};

export default function SensorUseDetail({ route, navigation }: any) {
    const { scale, s } = useScale();
    const styles = useMemo(() => makeStyles(s), [scale]);
    const [__p] = useState(() => {
        if (typeof window === 'undefined') return route?.params || {};
        try {
            const id = route?.params?.id;
            const key = `_navParams_AnalysisDetail_${id ?? ''}`;
            const saved = window.localStorage.getItem(key);
            if (saved) return JSON.parse(saved);
        } catch {}
        return route?.params || {};
    });
    const [analyze, setAnalyze] = useState<any>({
        data: [],
        headers: [],
        filename: 'csvdata.csv'
    });
    const data = __p?.data || {};
    const isFocused = useIsFocused();
    const { dark, setDark } = useDarkMode();

    useEffect(() => {
        getStorage('darkMode').then((v: any) => { if (v !== null) setDark(v); });
        navigation.setOptions({
            headerStyle: { backgroundColor: '#304156', elevation: 0, shadowOpacity: 0, borderBottomWidth: 0 },
            headerTintColor: '#fff',
            headerTitleStyle: { color: '#fff', fontSize: s(20) },
        });
    }, [isFocused]);

    const T = dark ? DARK : LIGHT;

    const [daily, setDaily] = useState<any>(null);
    const [month, setMonth] = useState<any>(null);
    const [year, setYear] = useState<any>(null);
    const [info, setInfo] = useState<any[]>([]);
    const sensorInfoData = info.length > 0 ? info[0] : {};
    const [weeklyData, setWeeklyData] = useState<any[]>([]);
    const [monthlyData, setMonthlyData] = useState<any[]>([]);
    const [search, setSearch] = useState<any[]>([]);
    const [xTickFontSize, setXTickFontSize] = useState(16);
    const [graphSize, setGraphSize] = useState({ width: 0, height: 0, barSize: 25 });

    // 날짜 선택용 state
    const today = moment();
    const start14 = moment().subtract(13, 'days');
    const [startDate, setStartDate] = useState<Date>(
        moment().subtract(13, 'days').toDate()
    );
    const [endDate, setEndDate] = useState<Date>(new Date());
    const years = [moment().year() - 1, moment().year(), moment().year() + 1];
    const months = Array.from({ length: 12 }, (_, i) => i + 1);
    const days = Array.from({ length: 31 }, (_, i) => i + 1);

    const emptyMonthlyData = Array.from({ length: 12 }, (_, i) => ({ month: `${i+1}월`, count: 0 }));
    const chartMonthlyData = monthlyData.length > 0 ? monthlyData : emptyMonthlyData;

    const [weeklySize, setWeeklySize] = useState({ width: 0, height: 0 });
    const [monthlySize, setMonthlySize] = useState({ width: 0, height: 0 });

    const [errorHistory, setErrorHistory] = useState<any>(null);
    const [errorSummary, setErrorSummary] = useState<any>(null);
    const [searchRange, setSearchRange] = useState<{ start: string; end: string } | null>(null);
    const [errorTimeline, setErrorTimeline] = useState<any[]>([]);

    const [loading, setLoading] = useState(false);
    const [autoSearch, setAutoSearch] = useState(false);
    const [monthOpen, setMonthOpen] = useState(false);
    const monthDropRef = useRef<any>(null);
    const monthPortalRef = useRef<any>(null);
    const [monthDropPos, setMonthDropPos] = useState({ top: 0, left: 0, width: 0 });

    useEffect(() => {
        if (!monthOpen) return;
        const handler = (e: any) => {
            if (monthDropRef.current && !monthDropRef.current.contains(e.target) &&
                monthPortalRef.current && !monthPortalRef.current.contains(e.target)) {
                setMonthOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [monthOpen]);

    const openMonthDrop = () => {
        if (monthDropRef.current) {
            const rect = monthDropRef.current.getBoundingClientRect();
            setMonthDropPos({ top: rect.bottom + 4, left: rect.left, width: Math.max(rect.width, 150) });
        }
        setMonthOpen(!monthOpen);
    };

    const SENSITIVITY_LABEL: Record<number, string> = {0: "강", 1: "중", 2: "약"};

    const [manager, setManager] = useState<any[]>([]);
    function getManagerText(list: any[]) {
    if (!Array.isArray(list) || list.length === 0) return '없음';

    return list
        .map(item => item.NAME)
        .filter(Boolean)
        .join(', ');
    }
    const [MemosList, setMemosList] = useState<any[]>([]);
    const statusMap: { [key: number]: string } = {
        1: '욕창',
        2: '욕창위험',
        3: '낙상위험',
        4: '요로감염'
    };
    const STATUS_LABEL: any = {
        "0": "정상",
        "1": "에러",
        "2": "알수없음"
    };
    function getStatusTextList(memos: any[]) {
        return Array.from(
            new Set(
                memos
                    .map(item => statusMap[item.STATUS])
                    .filter(Boolean)
            )
        ).join(', ');
    }
    const memoStatusText = getStatusTextList(MemosList);
    const SensorInfo = [
        { label: '이름', key: 'NAME', source: 'base' },
        { label: '아이디', key: 'SENSOR_ID', source: 'base' },
        { label: '구역', key: 'AREA', source: 'base' },
        { label: '담당 보호사', key: 'MANAGER', source: 'custom' },
        { label: '특이사항(병증)', key: 'MEMO_STATUS', source: 'custom' },
        { label: '전월 사용일', key: 'USE_DAYS', source: 'custom' },
        { label: '센서 교체 횟수', key: 'SENSOR_COUNT', source: 'stat' },
    ];
    const useDays =
    sensorInfoData?.USE_DAYS
        ? sensorInfoData.USE_DAYS + '일'
        : '-';
    const sourceMap = {
        base: data,
        stat: sensorInfoData,
        custom: {
            MEMO_STATUS: memoStatusText || '없음',
            MANAGER: getManagerText(manager),
            USE_DAYS: useDays
        }
    };
    const downloadExcel = async () => {
    try {
        const STARTDATE = moment(startDate).format('YYYY-MM-DD');
        const ENDDATE = moment(endDate).format('YYYY-MM-DD');

        const response = await fetch('/sensor/dateTimeFilteredHistory2/excel', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(await authHeader()),
        },
        body: JSON.stringify({
            SENSOR_ID: data.SENSOR_ID,
            NAME: data.NAME,
            PERSON_IDX: data.PERSON_IDX,
            STARTDATE,
            ENDDATE
        }),
        });

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${data.SENSOR_ID}_${STARTDATE}_${ENDDATE}.xlsx`;
        document.body.appendChild(link);
        link.click();
    } catch (err) {
        console.error('엑셀 다운로드 실패:', err);
    }
    };

        useEffect(() => {
        Data.fetchData(
            'sensor/SensorUseByName',
            { NAME: data.NAME, SENSOR_ID: data.SENSOR_ID },
            (response: any) => {
                const arr = Array.isArray(response) ? response : Array.isArray(response.result) ? response.result : [];
                if (arr.length === 0) { setInfo([]); return; }
                const formatted = arr.map(row => ({
                    ...row,
                    BIRTH: row?.BIRTH ? moment(row.BIRTH).format('YYYY-MM-DD') : null
                }));
                setInfo(formatted);
            },
            true
        );

        Data.fetchData(
            'sensor/DailyReplaceInd',
            { SENSOR_ID: data.SENSOR_ID},
            (result: any, count: any) => {
                if (count > 0 && result.length > 0) {
                    const todayVal = result[0][7] ?? result[0]['7'] ?? 0;
                    const avgVal = result[0]['AVG'] || 0;
                    setDaily({ today: todayVal, avg: avgVal });

                    const weekArr: any[] = [];
                    for (let i = 1; i <= 7; i++) {
                        const dateLabel = moment().subtract(7 - i, 'days').format('MM/DD');
                        weekArr.push({ day: dateLabel, count: result[0][i] ?? 0 });
                    }
                    weekArr.push({ day: '평균', count: avgVal });
                    setWeeklyData(weekArr);
                } else {
                    setDaily({ today: 0, avg: 0 });
                    setWeeklyData([]);
                }
            },
            true
        );

        Data.fetchData(
            'sensor/MonthlyReplaceInd',
            { SENSOR_ID: data.SENSOR_ID },
            (result: any, count: any) => {
                if (count > 0 && result.length > 0) {
                    const monthlyObj = result[0];
                    const monthLabels = ['1월','2월','3월','4월','5월','6월','7월','8월','9월','10월','11월','12월'];
                    const monthlyArr = monthLabels.map(label => ({ month: label, count: monthlyObj[label] ?? 0 }));
                    const avg = monthlyObj['AVG'] || 0;
                    monthlyArr.push({ month: '평균', count: avg });
                    setMonthlyData(monthlyArr);
                    setMonth({ avg });
                } else {
                    setMonth({ avg: 0 });
                    setMonthlyData([]);
                }
            },
            true
        );

        Data.fetchData(
            'sensor/YearReplaceInd',
            { SENSOR_ID: data.SENSOR_ID },
            (result: any, count: any) => {
                setYear(count > 0 && result.length > 0 ? result[0].TOTAL || 0 : 0);
            },
            true
        );
        const STARTDATE = moment().subtract(13, 'days');
        const ENDDATE = moment();

        Data.fetchData(
            'sensor/DailyReplacement',
            {
                SENSOR_ID: data.SENSOR_ID,
                STARTDATE: STARTDATE.format('YYYY-MM-DD'),
                ENDDATE: ENDDATE.format('YYYY-MM-DD'),
            },
            (result: any[]) => {
                const temp = [];
                for (let m = moment(STARTDATE); m.isSameOrBefore(ENDDATE); m.add(1, 'days')) {
                    const d = m.format('YYYY-MM-DD');
                    const found = result.find(r => r.DATE === d);
                    temp.push({ DATE: d, COUNT: found ? found.COUNT : 0 });
                }
                setSearch(temp);
            },
            true
        );

        Data.fetchData(
            'sensor/dateTimeFilteredHistory2',
            {
                SENSOR_ID: data.SENSOR_ID,
                STARTDATE: STARTDATE.format('YYYY-MM-DD'),
                ENDDATE: ENDDATE.format('YYYY-MM-DD'),
            },
            (result: any[], count: number) => {
                if (count > 0) {
                    const mapped = result.map((item: any, idx: number) => {
                        return {
                            ...item,
                            start: moment(item.start).format('YYYY:MM:DD HH:mm'),
                            end: moment(item.end).format('YYYY:MM:DD HH:mm'),
                            statusRaw: item.status,
                            status: STATUS_LABEL[item.status] ?? item.status,
                            caregivers: Array.isArray(item.caregivers) ? item.caregivers : [],
                            STATUS_LIST: Array.isArray(item.STATUS_LIST)
                            ? item.STATUS_LIST
                                .map((s: number) => statusMap[s] ?? s)
                                .join(', ')
                            : statusMap[item.STATUS_LIST] ?? item.STATUS_LIST,
                            SENSITIVITY:
                                item.SENSITIVITY === null || item.SENSITIVITY === undefined
                                    ? ""
                                    : SENSITIVITY_LABEL[item.SENSITIVITY] ?? item.SENSITIVITY
                        };
                    });

                    setErrorHistory(mapped);
                    setErrorSummary(mapped[mapped.length - 1]);

                    if (mapped.length >= 2) {
                        setSearchRange({
                            start: mapped[0].end,
                            end: mapped[mapped.length - 2].start,
                        });
                    }

                    setErrorTimeline(
                        mapped
                            .filter(i => Number(i.statusRaw) === 1)
                            .map(i => ({ start: i.start, end: i.end }))
                    );

                    setAnalyze({
                        data: mapped,
                        headers: [
                            { label: "SENSOR_ID", key: "SENSOR_ID" },
                            { label: "시작 시간", key: "start" },
                            { label: "종료 시간", key: "end" },
                            { label: "상태", key: "status" },
                            { label: "감지량", key: "maxValue" },
                            { label: "담당 보호사", key: "caregivers" },
                            { label: "특이사항", key: "STATUS_LIST" },
                            { label: "센서 감도", key: "SENSITIVITY" },
                            { label: "정상 건수", key: "Normal" },
                            { label: "에러 건수", key: "Error" },
                            { label: "미확인", key: "Unknown" },
                            { label: "정확도(%)", key: "percentage" }
                        ],
                        filename: `${data.SENSOR_ID}_Analyze_${STARTDATE.format('YYYYMMDD')}_${ENDDATE.format('YYYYMMDD')}.csv`
                    });
                } else {
                    setErrorHistory([]);
                    setErrorTimeline([]);
                    setErrorSummary({
                        Normal: 0,
                        Error: 0,
                        Unknown: 0,
                        percentage: 0
                    });
                }

                setLoading(false);
            },
            true
        );
        Data.fetchData(
            'tabSensor/getMemosList',
            {
                SENSOR_ID: data.SENSOR_ID,
                NAME: data.NAME,
                BIRTH: data.BIRTH
                    ? moment(data.BIRTH).format('YYYY-MM-DD')
                    : ''
            },
            (result: any, count: any) => {
                if (count > 0) { setMemosList(result); } else { setMemosList([]); }
            },
            true
        );
        Data.fetchData(
            'sensor/getSensorInChargeList',
            { SENSOR_ID: data.SENSOR_ID },
            (result: any, count: any) => {
                if (count !== -1) { setManager(result); } else { setManager([]); }
            },
            true
        );
    }, [isFocused]);


    const csvRef = useRef<any>(null);

    const downloadCSV = () => {
        if (!analyze?.data || analyze.data.length === 0) {
            alert("다운로드할 데이터가 없습니다.");
            return;
        }
        csvRef.current?.link?.click();
    };
    useEffect(() => {
        if (autoSearch) {
            setAutoSearch(false);
            fetchData();
        }
    }, [autoSearch]);

    const fetchData = () => {
        if (!startDate || !endDate) {
            alert("날짜를 선택해주세요.");
            return;
        }

        const STARTDATE = moment(startDate);
        const ENDDATE = moment(endDate);

        if (ENDDATE.isBefore(STARTDATE)) {
            alert("종료일은 시작일보다 빠를 수 없습니다.");
            return;
        }
        const diffDays = ENDDATE.diff(STARTDATE, "days") + 1;

        if (diffDays > 31) {
            setTimeout(() => {
                alert("검색 기간은 최대 31일까지만 가능합니다.");
            }, 100);
            return;
        }

    setLoading(true);

    let finishedCount = 0;
    const TOTAL_FETCH = 2;
    const done = () => {
        finishedCount += 1;
        if (finishedCount === TOTAL_FETCH) {
            setLoading(false);
        }
    };

        Data.fetchData(
            "sensor/DailyReplacement",
            {
                SENSOR_ID: data.SENSOR_ID,
                STARTDATE: STARTDATE.format("YYYY-MM-DD"),
                ENDDATE: ENDDATE.format("YYYY-MM-DD"),
            },
            (result: any[], count: number) => {
                const temp: any[] = [];
                for (let m = moment(STARTDATE); m.isSameOrBefore(ENDDATE); m.add(1, "days")) {
                    const dateStr = m.format("YYYY-MM-DD");
                    const found = result.find((r) => r.DATE === dateStr);
                    temp.push({ DATE: dateStr, COUNT: found ? found.COUNT : 0 });
                }
                setSearch(temp);

                let newBarSize = 25;
                if (diffDays > 14 && diffDays <= 20) {
                    setXTickFontSize(14);
                    newBarSize = 22;
                } else if (diffDays > 20) {
                    setXTickFontSize(12);
                    newBarSize = 20;
                } else {
                    setXTickFontSize(16);
                }
                setGraphSize(prev => ({ ...prev, barSize: newBarSize }));
                done();
            },
            true
        );
        const analyzeEndDate = moment(ENDDATE).add(1, 'days');
        const analyzeHeaders = [
            { label: "SENSOR_ID", key: "SENSOR_ID" },
            { label: "시작 시간", key: "start" },
            { label: "종료 시간", key: "end" },
            { label: "상태", key: "status" },
            { label: "감지량", key: "maxValue" },
            { label: "담당 보호사", key: "caregivers" },
            { label: "특이사항", key: "STATUS_LIST" },
            { label: "센서 감도", key: "SENSITIVITY" },
            { label: "정상 건수", key: "Normal" },
            { label: "에러 건수", key: "Error" },
            { label: "미확인", key: "Unknown" },
            { label: "정확도(%)", key: "percentage" }
        ];
        const analyzeFilename = data.SENSOR_ID + '_Analyze_' + STARTDATE.format('YYYYMMDD') + '_' + ENDDATE.format('YYYYMMDD');

        Data.fetchData(
            'sensor/dateTimeFilteredHistory2',
            {
                SENSOR_ID: data.SENSOR_ID,
                STARTDATE: STARTDATE.format('YYYY-MM-DD'),
                ENDDATE: ENDDATE.format('YYYY-MM-DD'),
            },
            (result: any, count: any) => {

                if (count > 0) {
                    const mapped = result.map((item: any, idx: number) => {
                        return {
                            ...item,
                            start: moment(item.start).format('YYYY:MM:DD HH:mm'),
                            end: moment(item.end).format('YYYY:MM:DD HH:mm'),
                            statusRaw: item.status,
                            status: STATUS_LABEL[item.status] ?? item.status,
                            caregivers: Array.isArray(item.caregivers) ? item.caregivers : [],
                            STATUS_LIST: Array.isArray(item.STATUS_LIST)
                            ? item.STATUS_LIST
                                .map((s: number) => statusMap[s] ?? s)
                                .join(', ')
                            : statusMap[item.STATUS_LIST] ?? item.STATUS_LIST,

                            SENSITIVITY:
                                item.SENSITIVITY === null || item.SENSITIVITY === undefined
                                    ? ""
                                    : SENSITIVITY_LABEL[item.SENSITIVITY] ?? item.SENSITIVITY
                        };
                    });

                    setErrorHistory(mapped);

                    const summary = mapped[mapped.length - 1];
                    setErrorSummary(summary);

                    if (mapped.length >= 2) {
                        const lastDataRow = mapped[mapped.length - 2];
                        const secondDataRow = mapped[0];

                        setSearchRange({
                            start: secondDataRow.end,
                            end: lastDataRow.start,
                        });
                    }
                    const errorOnlyTimeline = mapped
                        .filter(item => item.statusRaw !== null && item.statusRaw !== undefined && item.statusRaw !== '')
                        .filter(item => Number(item.statusRaw) === 1)
                        .map(item => ({
                            start: item.start,
                            end: item.end,
                        }));

                    setErrorTimeline(errorOnlyTimeline);

                    setAnalyze({
                        data: mapped,
                        headers: analyzeHeaders,
                        filename: `${analyzeFilename}.csv`
                    });
                } else {
                    console.warn('[NO DATA] count = 0');
                }
                done();
            },
            true
        );
    };

    // ─── 테마별 색상 ───
    const statColors = [
        { bg: dark ? 'rgba(56,189,248,0.15)' : '#dbeafe', text: S.accent },
        { bg: dark ? 'rgba(251,146,60,0.15)' : '#ffedd5', text: S.warn },
        { bg: dark ? 'rgba(52,211,153,0.15)' : '#dcfce7', text: S.ok },
        { bg: dark ? 'rgba(167,139,250,0.15)' : '#ede9fe', text: S.purple },
    ];
    const gridColor = dark ? 'rgba(255,255,255,0.08)' : '#e5e7eb';
    const tickColor = dark ? '#94A3B8' : '#6b7280';
    const barFill1 = dark ? S.accent : '#8884d8';
    const barFill2 = dark ? S.ok : '#82ca9d';
    const btnBg = dark ? S.accent : '#4f46e5';

const { width, height } = useWindowDimensions();
    return (
        <View style={[styles.container, { backgroundColor: T.bg }]}>
            <View id="datepicker-portal-root" />
            <ScrollView contentContainerStyle={styles.scrollContent}>
                {/* 상단 카드 */}
                <View style={[styles.statRow, { height: height * 0.10}]}>
                    {[
                        { title: '금일 기저귀 교체', value: daily?.today },
                        { title: '최근 7일 평균', value: daily?.avg },
                        { title: '올해 누적 평균', value: month?.avg },
                        { title: '누적 교체 횟수', value: year },
                    ].map((item, idx) => (
                        <View key={idx} style={[styles.statCard, { backgroundColor: statColors[idx]?.bg, borderColor: T.border, borderWidth: 0.5 }]}>
                            <Text style={[styles.statTitle, { color: T.textSub }]}>{item.title}</Text>
                            <Text style={[styles.statValue, { color: statColors[idx]?.text }]}>{fmt(item.value)}</Text>
                        </View>
                    ))}
                </View>

                {/* 센서 정보 + 상단 그래프 */}
                <View style={[styles.rowContainer, { height: height * 0.36 }]}>
                    <View style={[styles.infoSection, { backgroundColor: T.card, borderColor: T.border }]}>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: s(8) }}>
                        <Text style={[styles.sectionTitle, { color: T.text, marginBottom: s(0) }]}>센서 정보</Text>
                    </View>

                    <ScrollView
                        style={{ flex: 1 }}
                        showsVerticalScrollIndicator={false}
                    >
                        <View style={[styles.table, { borderColor: T.border }]}>
                        {SensorInfo.map((item, idx) => (
                            <View key={idx} style={[styles.row, { borderColor: T.border }]}>
                            <Text style={[styles.label, { color: T.textSub }]}>{item.label}</Text>
                            <Text style={[styles.value, { color: T.text }]}>
                                {item.source === 'custom' ? (sourceMap[item.source]?.[item.key] ?? '-') : fmt(sourceMap[item.source]?.[item.key])}
                            </Text>
                            </View>
                        ))}
                        </View>
                    </ScrollView>
                    </View>

                    {/* 그래프 + 날짜검색 */}
                    <View style={[styles.graphSection, { flex: 5, marginLeft: s(12), backgroundColor: T.card, borderColor: T.border }]}
                        onLayout={(e) => { const { width, height } = e.nativeEvent.layout; setGraphSize(prev => ({ ...prev, width, height })); }}>

                        {/* 날짜 선택 한 줄 */}
                        <View style={styles.filterRow}>
                            {/* 월 선택 드롭다운 */}
                            <View ref={monthDropRef}>
                                <TouchableOpacity
                                    style={[styles.monthDropdown, { borderColor: T.border, backgroundColor: T.cardInner }]}
                                    onPress={openMonthDrop}
                                >
                                    <Text style={{ fontSize: s(13), fontWeight: '600', color: T.text }}>월 선택</Text>
                                    <AntDesign name={monthOpen ? 'up' : 'down'} size={s(10)} color={T.muted} style={{ marginLeft: s(4) }} />
                                </TouchableOpacity>
                                {monthOpen && createPortal(
                                    <div ref={monthPortalRef} style={{
                                        position: 'fixed', top: monthDropPos.top, left: monthDropPos.left,
                                        width: monthDropPos.width, zIndex: 99999,
                                        backgroundColor: dark ? '#1E293B' : '#FFFFFF',
                                        border: `1px solid ${dark ? 'rgba(99,179,237,0.12)' : '#E5E7EB'}`,
                                        borderRadius: s(10), overflow: 'hidden',
                                        boxShadow: dark ? '0 4px 20px rgba(0,0,0,0.5)' : '0 4px 20px rgba(0,0,0,0.12)',
                                        maxHeight: s(280), overflowY: 'auto',
                                    }}>
                                        {Array.from({ length: 12 }, (_, i) => {
                                            const m = moment().subtract(i, 'months');
                                            return (
                                                <div
                                                    key={m.format('YYYY-MM')}
                                                    onClick={() => {
                                                        setMonthOpen(false);
                                                        const mc = moment(m.format('YYYY-MM'), 'YYYY-MM');
                                                        setStartDate(mc.startOf('month').toDate());
                                                        setEndDate(mc.clone().endOf('month').toDate());
                                                        setAutoSearch(true);
                                                    }}
                                                    style={{
                                                        padding: '9px 14px', cursor: 'pointer',
                                                        borderBottom: `0.5px solid ${dark ? 'rgba(99,179,237,0.12)' : '#E5E7EB'}`,
                                                        color: dark ? '#E2E8F0' : '#111827', fontSize: s(13),
                                                    }}
                                                    onMouseEnter={(e: any) => { e.currentTarget.style.backgroundColor = dark ? '#0F172A' : '#F3F4F6'; }}
                                                    onMouseLeave={(e: any) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                                >
                                                    {m.format('YYYY년 MM월')}
                                                </div>
                                            );
                                        })}
                                    </div>,
                                    document.body
                                )}
                            </View>

                            <View style={{ width: s(1), height: s(20), backgroundColor: T.border, marginHorizontal: s(4) }} />

                            <DatePicker
                                selected={startDate}
                                onChange={(date: Date) => setStartDate(date)}
                                dateFormat="yyyy-MM-dd"
                                portalId="datepicker-portal-root"
                                customInput={
                                    <View style={[styles.dateInput, { borderColor: T.border, backgroundColor: T.cardInner }]}>
                                        <Text style={[styles.dateText, { color: T.text }]}>
                                            {moment(startDate).format('YYYY-MM-DD')}
                                        </Text>
                                    </View>
                                }
                            />

                            <Text style={[styles.tilde, { color: T.textSub }]}>~</Text>

                            <DatePicker
                                selected={endDate}
                                onChange={(date: Date) => setEndDate(date)}
                                dateFormat="yyyy-MM-dd"
                                portalId="datepicker-portal-root"
                                customInput={
                                    <View style={[styles.dateInput, { borderColor: T.border, backgroundColor: T.cardInner }]}>
                                        <Text style={[styles.dateText, { color: T.text }]}>
                                            {moment(endDate).format('YYYY-MM-DD')}
                                        </Text>
                                    </View>
                                }
                            />
                            <TouchableOpacity style={[styles.searchBoxRow, { backgroundColor: btnBg }]} onPress={fetchData}>
                                <Text style={styles.searchText}>검색</Text>
                            </TouchableOpacity>

                            <TouchableOpacity style={[styles.searchBoxRow, { backgroundColor: btnBg }]} onPress={downloadCSV}>
                                <Text style={styles.searchText}>CSV 다운로드</Text>
                            </TouchableOpacity>
                            <TouchableOpacity style={[styles.searchBoxRow, { backgroundColor: btnBg }]} onPress={downloadExcel}>
                                <Text style={styles.searchText}>엑셀 다운로드</Text>
                            </TouchableOpacity>
                            <CSVLink {...analyze} ref={csvRef} style={{ display: 'none' }} />
                        </View>
                        {search.length > 0 && graphSize.width > 0 && (
                            <BarChart
                                width={graphSize.width}
                                height={graphSize.height - 40}
                                data={search.map(item => ({ ...item, dayLabel: moment(item.DATE).format('MM/DD') }))}
                                margin={{ top: 30, right: 5, left: -30, bottom: 15 }}
                                barCategoryGap="10%"
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                                <XAxis dataKey="dayLabel" interval={0} tick={{ fontSize: xTickFontSize, fill: tickColor }}/>
                                <YAxis allowDecimals={false} tick={{ fill: tickColor }} tickFormatter={(v: number) => v.toLocaleString()} />
                                <Tooltip contentStyle={{ backgroundColor: T.card, borderColor: T.border, color: T.text }} formatter={(v: any) => [fmt(v), '횟수']} />
                                <Bar dataKey="COUNT" fill={barFill1} barSize={graphSize.barSize || 25}>
                                    <LabelList position="top" dataKey="COUNT" style={{ fill: T.text, fontSize: 12 }} formatter={(v: any) => fmt(v)} />
                                </Bar>
                            </BarChart>
                        )}
                    </View>
                </View>
                <View style={[styles.bottomGraphContainer, { height: height * 0.36 }]}>
                    {/* 센서 오류 분석 */}
                    <View style={[styles.bottomgraphSection, { backgroundColor: T.card, borderColor: T.border }]}>
                        <Text style={{ alignSelf: 'flex-start', fontSize: s(16), fontWeight: '600', marginBottom: s(8), color: T.text }}>
                            센서 오류 분석
                        </Text>

                        {/* 내부 좌우 분할 */}
                        <View style={{ flexDirection: 'row', width: '100%', flex: 1 }}>

                            {/* 왼쪽 */}
                            <View style={{ flex: 2, paddingLeft: s(8), paddingRight: s(8), marginTop: s(2), borderLeftWidth: 1, borderLeftColor: T.border }}>
                                <View style={styles.errorSummary}>
                                    <View style={[styles.errorCard, { backgroundColor: dark ? 'rgba(52,211,153,0.12)' : '#f0fdf4' }]}>
                                        <Text style={[styles.errorLabel, { color: T.textSub }]}>정상</Text>
                                        <Text style={[styles.errorValue, { color: S.ok }]}>
                                            {loading ? '-' : fmt(errorSummary?.Normal)}
                                        </Text>
                                    </View>
                                    <View style={[styles.errorCard, { backgroundColor: dark ? 'rgba(248,113,113,0.12)' : '#fef2f2' }]}>
                                        <Text style={[styles.errorLabel, { color: T.textSub }]}>에러</Text>
                                        <Text style={[styles.errorValue, { color: S.err }]}>
                                            {loading ? '-' : fmt(errorSummary?.Error)}
                                        </Text>
                                    </View>

                                    <View style={[styles.errorCard, { backgroundColor: dark ? 'rgba(148,163,184,0.12)' : '#f9fafb' }]}>
                                        <Text style={[styles.errorLabel, { color: T.textSub }]}>알수없음</Text>
                                        <Text style={[styles.errorValue, { color: T.muted }]}>
                                            {loading ? '-' : fmt(errorSummary?.Unknown)}
                                        </Text>
                                    </View>
                                </View>
                                <View style={[styles.accuracyBox, { backgroundColor: dark ? 'rgba(56,189,248,0.1)' : '#eef2ff' }]}>
                                    <Text style={[styles.accuracyLabel, { color: dark ? S.accent : '#4338ca' }]}>정확도</Text>
                                    <Text style={[styles.accuracyValue, { color: dark ? S.accent : '#4338ca' }]}>
                                        {loading ? '-%' : errorSummary?.percentage ?? '-%'}
                                    </Text>
                                </View>
                                {!loading && searchRange?.start && searchRange?.end && (
                                 <Text style={{ textAlign: 'center', fontSize: s(14), color: T.textSub, marginTop: s(22) }}>
                                    검색 기간 : {searchRange.start} ~ {searchRange.end}
                                </Text>
                                )}

                                {!loading && (!searchRange?.start || !searchRange?.end) && (
                                <Text style={{ textAlign: 'center', fontSize: s(14), color: T.muted, marginTop: s(22) }}>
                                    해당 날짜에 대한 기록이 없습니다.
                                </Text>
                                )}
                            </View>
                            {/* 오른쪽 */}
                            <View style={{ flex: 1, paddingLeft: s(12), borderLeftWidth: 1, borderLeftColor: T.border }}>
                                <Text style={[styles.sectionTitle, { fontSize: s(14), color: T.text }]}>오류 타임라인</Text>

                                {loading ? (
                                    <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
                                        <ActivityIndicator size="small" color={S.err} />
                                        <Text style={{ marginTop: s(6), fontSize: s(13), color: T.muted }}>
                                            데이터 불러오는 중...
                                        </Text>
                                    </View>
                                ) : (
                                    <ScrollView
                                        style={{ flex: 1, marginTop: s(6) }}
                                        showsVerticalScrollIndicator={false}
                                    >
                                        {errorTimeline && errorTimeline.length > 0 ? (
                                            errorTimeline.map((item, idx) => (
                                                <View key={idx} style={[styles.timelineItem, { borderBottomColor: T.border }]}>
                                                    <Text style={styles.timelineText}>
                                                        ⛔ {item.start} ~ {item.end}
                                                    </Text>
                                                </View>
                                            ))
                                        ) : (
                                            <Text style={[styles.placeholderText, { color: T.muted }]}>
                                                오류 발생 내역이 없습니다.
                                            </Text>
                                        )}
                                    </ScrollView>
                                )}
                            </View>
                        </View>
                    </View>
                        <View style={[styles.bottomgraphSection, { backgroundColor: T.card, borderColor: T.border }]}
                        onLayout={(e) => {
                            const { width, height } = e.nativeEvent.layout;
                            setMonthlySize({ width, height });
                        }}
                        >
                        <Text style={{ alignSelf: 'flex-start', fontSize: s(16), fontWeight: '600',  marginBottom: s(8), color: T.text }}>
                            월간
                        </Text>
                        {monthlySize.width > 0 && (
                            <BarChart
                            width={monthlySize.width}
                            height={monthlySize.height - 40}
                            data={chartMonthlyData}
                            margin={{ top: 25, right: 5, left: -25, bottom: 15 }}
                            barCategoryGap="10%"
                            >
                            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                            <XAxis dataKey="month" interval={0} tick={{ fill: tickColor }} />
                            <YAxis allowDecimals={false} tick={{ fill: tickColor }} tickFormatter={(v: number) => v.toLocaleString()} />
                            <Tooltip contentStyle={{ backgroundColor: T.card, borderColor: T.border, color: T.text }} formatter={(v: any) => [fmt(v), '횟수']} />
                            <Bar
                                dataKey="count"
                                fill={barFill2}
                                barSize={monthlySize.barSize || 25}
                            >
                                <LabelList dataKey="count" position="top" style={{ fill: T.text, fontSize: 12 }} formatter={(v: any) => fmt(v)} />
                            </Bar>
                            </BarChart>
                        )}
                        </View>
                    </View>

            </ScrollView>
        </View>
    );
}

const makeStyles = (s: ScaleFn) => StyleSheet.create({
  /* ===== 기본 ===== */
    container: {
    flex: 1,
    },
    scrollContent: {
    padding: s(16),
    },
  /* ===== 상단 통계 카드 ===== */
    statRow: {
    flexDirection: 'row',
    gap: s(8),
    marginBottom: s(16),
    },
    statCard: {
    flex: 1,
    paddingVertical: s(14),
    borderRadius: s(14),
    alignItems: 'center',
    justifyContent: 'center',
    },
    statTitle: {
    fontSize: s(14),
    },
    statValue: {
    fontSize: s(20),
    fontWeight: '700',
    marginTop: s(4),
    },
    iconBtn: {
    width: s(34),
    height: s(34),
    borderRadius: s(10),
    justifyContent: 'center',
    alignItems: 'center',
    },
  /* ===== 메인 레이아웃 ===== */
    rowContainer: {
    flexDirection: 'row',
    gap: s(12),
    marginBottom: s(16),
    },
  /* ===== 센서 정보 테이블 ===== */
    infoSection: {
    flex: 2,
    padding: s(14),
    borderRadius: s(14),
    borderWidth: 0.5,
    },
    sectionTitle: {
    fontSize: s(16),
    fontWeight: '600',
    marginBottom: s(8),
    },
    table: {
    borderTopWidth: 1,
    },
    row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: s(8),
    borderBottomWidth: 1,
    },
    label: {
    fontSize: s(14),
    },
    value: {
    fontSize: s(14),
    fontWeight: '500',
    },
  /* ===== 상단 그래프 영역 ===== */
    graphSection: {
    flex: 5,
    padding: s(14),
    borderRadius: s(14),
    borderWidth: 0.5,
    alignItems: 'center',
    },
  /* ===== 날짜 필터 ===== */
    filterRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: s(6),
    rowGap: s(6),
    marginBottom: s(8),
    zIndex: 9999,
    },
    dateInput: {
    height: s(32),
    minWidth: s(110),
    flexShrink: 1,
    paddingHorizontal: s(10),
    borderRadius: s(8),
    borderWidth: 1,
    justifyContent: 'center',
    },
    dateText: {
    fontSize: s(14),
    fontWeight: '500',
    },
    tilde: {
    fontSize: s(14),
    },
    searchBoxRow: {
    height: s(32),
    paddingHorizontal: s(12),
    borderRadius: s(8),
    alignItems: 'center',
    justifyContent: 'center',
    },
    searchText: {
    fontSize: s(13),
    fontWeight: '600',
    color: '#ffffff',
    },
  /* ===== 월 드롭다운 ===== */
    monthDropdown: {
    flexDirection: 'row',
    alignItems: 'center',
    height: s(32),
    minWidth: s(90),
    paddingHorizontal: s(10),
    borderRadius: s(8),
    borderWidth: 1,
    },
    dropdownList: {
    position: 'absolute',
    top: s(36),
    left: s(0),
    minWidth: s(140),
    zIndex: 9999,
    borderWidth: 1,
    borderRadius: s(10),
    overflow: 'hidden',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: s(12),
    elevation: 8,
    maxHeight: s(300),
    },
    dropdownItem: {
    paddingVertical: s(9),
    paddingHorizontal: s(14),
    borderBottomWidth: 0.5,
    },
  /* ===== 하단 그래프 레이아웃 ===== */
    bottomGraphContainer: {
    flexDirection: 'row',
    gap: s(10),
    marginTop: s(12),
    },
    bottomgraphSection: {
    flex: 1,
    padding: s(14),
    borderRadius: s(14),
    borderWidth: 0.5,
    alignItems: 'center',
    },
  /* ===== 오류 분석 ===== */
    errorSummary: {
    flexDirection: 'row',
    gap: s(8),
    marginBottom: s(10),
    },
    errorCard: {
    flex: 1,
    paddingVertical: s(12),
    borderRadius: s(10),
    alignItems: 'center',
    justifyContent: 'center',
    },
    errorLabel: {
    fontSize: s(14),
    },
    errorValue: {
    fontSize: s(18),
    fontWeight: '700',
    marginTop: s(2),
    },
  /* ===== 정확도 박스 ===== */
    accuracyBox: {
    width: '100%',
    marginTop: s(8),
    paddingVertical: s(14),
    borderRadius: s(10),
    alignItems: 'center',
    },
    accuracyLabel: {
    fontSize: s(14),
    },
    accuracyValue: {
    fontSize: s(18),
    fontWeight: '800',
    },
  /* ===== 기타, 타임라인 ===== */
    placeholderBox: {
    height: s(120),
    width: '100%',
    marginTop: s(6),
    },
    placeholderText: {
    fontSize: s(14),
    textAlign: 'center',
    },
    timelineItem: {
    paddingVertical: s(4),
    borderBottomWidth: 1,
    },
    timelineText: {
    fontSize: s(13),
    color: '#dc2626',
    },
});
